import { PlusOutlined } from "@ant-design/icons";
import { faEye, faPencil, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Row, Table, Tooltip } from "antd";
import { FullHeightContainerLayout, ProhibitedArea } from "components";
import moment from "moment";
import React from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;

const DataSyndicationSourceList = ({
    items,
    onAddSource,
    onViewSource,
    onEditSource,
    onDeleteSource,
    onEnableDisableSource,
    permission = { canEdit: true, canView: true, canDelete: true, canAdd: true },
    selectedSourceId,
    onSourceSelected,
}) => {
    const [selectedRowKeys, setSelectedRowKeys] = React.useState(selectedSourceId ? [selectedSourceId] : []);

    React.useEffect(() => {
        if (selectedSourceId) {
            setSelectedRowKeys([selectedSourceId]);
        } else {
            setSelectedRowKeys([]);
        }
    }, [selectedSourceId]);

    const onRowClick = React.useCallback(
        (record) => {
            setSelectedRowKeys([record.sourceId]); // Supports single selection only
            onSourceSelected && onSourceSelected(record.sourceId);
        },
        [onSourceSelected],
    );

    const onRowSelectionChange = React.useCallback((sourceId) => {
        setSelectedRowKeys(sourceId);
    }, []);

    const onRow = React.useCallback(
        (record) => {
            return {
                onClick: () => onRowClick(record),
            };
        },
        [onRowClick],
    );

    if (!permission) {
        return <ProhibitedArea></ProhibitedArea>;
    }

    return (
        <FullHeightContainerLayout
            showHeader={false}
            showFooter={true}
            content={
                <Table
                    size="small"
                    rowKey="sourceId"
                    dataSource={items}
                    pagination={false}
                    scroll={{ y: "100vh" }}
                    className="container-height-100 syndication-source-list-table"
                    rootClassName="cursor-pointer"
                    data-testid={CypressTestIds.SYNDICATION_SOURCE_LIST_TABLE}
                    onRow={onRow}
                    rowSelection={{ type: "radio", selectedRowKeys, onChange: onRowSelectionChange }}
                >
                    <Column
                        width="6em"
                        render={(value, source, index) => (
                            <>
                                <Tooltip title="Edit source">
                                    <FontAwesomeIcon
                                        icon={faPencil}
                                        className={permission.canEdit ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                        onClick={() => (permission.canEdit ? onEditSource(source.sourceId) : null)}
                                        data-testid={CypressTestIds.SYNDICATION_SOURCE_LIST_EDIT_BUTTON}
                                    />{" "}
                                </Tooltip>
                                <Tooltip title="View source">
                                    <FontAwesomeIcon
                                        icon={faEye}
                                        className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                        onClick={() => (permission.canView ? onViewSource(source.sourceId) : null)}
                                        data-testid={CypressTestIds.SYNDICATION_SOURCE_LIST_VIEW_BUTTON}
                                    />{" "}
                                </Tooltip>
                            </>
                        )}
                    />
                    <Column
                        width="15%"
                        dataIndex="name"
                        title="Name"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, source, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        )}
                    />
                    <Column
                        width="15%"
                        dataIndex="description"
                        title="Purpose"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, source, index) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        )}
                    />
                    <Column
                        dataIndex="validFrom"
                        title="Valid From"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, source, index) =>
                            value && (
                                <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                    {moment.utc(value).toDate().toLocaleString()}
                                </Tooltip>
                            )
                        }
                    />
                    <Column
                        dataIndex="validUntil"
                        title="Valid Until"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, source, index) =>
                            value && (
                                <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                    {moment.utc(value).toDate().toLocaleString()}
                                </Tooltip>
                            )
                        }
                    />
                    <Column
                        dataIndex="invocationDate"
                        title="Last Invoked Date"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, source, index) =>
                            value && (
                                <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                    {moment.utc(value).toDate().toLocaleString()}
                                </Tooltip>
                            )
                        }
                    />
                    <Column
                        dataIndex="createdDate"
                        title="Created Date"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, source, index) => (
                            <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                {moment.utc(value).toDate().toLocaleString()}
                            </Tooltip>
                        )}
                    />
                    <Column
                        dataIndex="updatedDate"
                        title="Updated Date"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value, source, index) => (
                            <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                {moment.utc(value).toDate().toLocaleString()}
                            </Tooltip>
                        )}
                    />
                    <Column
                        width="5%"
                        render={(value, source, index) => {
                            return (
                                <Tooltip title="Delete source">
                                    <FontAwesomeIcon
                                        icon={faTrashCan}
                                        className={permission.canDelete ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                        onClick={() => (permission.canDelete ? onDeleteSource(source) : null)}
                                        data-testid={CypressTestIds.SYNDICATION_SOURCE_LIST_DELETE_BUTTON}
                                    />{" "}
                                </Tooltip>
                            );
                        }}
                    />
                </Table>
            }
            footer={
                <Row className="table-footer-row">
                    <Col span={24} className="footer-right-column">
                        <Button type="primary" disabled={!permission.canAdd} onClick={onAddSource} data-testid={CypressTestIds.SYNDICATION_SOURCE_LIST_ADD_BUTTON}>
                            <PlusOutlined /> Add Syndication Source
                        </Button>
                    </Col>
                </Row>
            }
            footerStyle={{ paddingTop: "1rem" }}
        ></FullHeightContainerLayout>
    );
};

export default DataSyndicationSourceList;
